/**
 * Wires `artistSettlements.model.ts` to its read and its one write.
 *
 * Calls `fetchArtistSettlements` and `useWorkThumbnails` for the list, and
 * `answerSettlement` to confirm or dispute a remittance — then refetches
 * rather than patching the row, because the record is in Postgres and a local
 * copy that disagreed would offer a control the database refuses.
 *
 * The state the model cannot hold is which settlement a request is in flight
 * for, which one has its dispute composer open, and the reload counter. A
 * dispute needs no note; an empty one is sent as null so that disagreeing is
 * never the harder of the two acts.
 */

import { useCallback, useEffect, useState } from "react";
import type { ArtistSettlement } from "@ondo/shared";

import { answerSettlement, fetchArtistSettlements } from "../lib/api.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import {
  describeArtistSettlementsError,
  toArtistSettlementsView,
} from "./artistSettlements.model.ts";
import type { ArtistSettlementsView } from "./artistSettlements.model.ts";

export interface ArtistSettlementsController {
  view: ArtistSettlementsView;
  /** The settlement a request is in flight for, or null. */
  working: string | null;
  /** The settlement whose dispute composer is open, or null. */
  disputing: string | null;
  openDispute: (settlementId: string) => void;
  closeDispute: () => void;
  confirm: (settlementId: string) => void;
  dispute: (settlementId: string, note: string) => void;
}

/**
 * What a gallery's covenant owes the artist, and their one control over it.
 *
 * ONE READ, unlike the gallery's two: there is no locked group here. A live
 * covenant is between the reseller and the gallery, and an artist who has not
 * been paid anything yet has nothing to read about a term they are not party
 * to.
 *
 * REFETCHED AFTER THE WRITE rather than patched in place, the rule the
 * gallery's controller keeps and for the same reason: the row in Postgres is
 * the record, and `assert_settlement_appended()` refuses a second answer — so a
 * local copy that disagreed would be a control offering something the database
 * would refuse.
 *
 * @param workBase '/collector/me/work' — where a row links to.
 */
export function useArtistSettlementsController(
  workBase: string,
): ArtistSettlementsController {
  const [settlements, setSettlements] = useState<ArtistSettlement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const images = useWorkThumbnails(settlements, (each) => ({
    id: each.workId,
    imagePath: each.imagePath,
  }));
  const [working, setWorking] = useState<string | null>(null);
  const [disputing, setDisputing] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let active = true;

    fetchArtistSettlements()
      .then((found) => {
        if (active) setSettlements(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeArtistSettlementsError(cause));
      });

    return () => {
      active = false;
    };
  }, [reloads]);

  const run = useCallback(async (key: string, act: () => Promise<void>) => {
    setWorking(key);
    try {
      await act();
      setError(null);
      setDisputing(null);
      setReloads((n) => n + 1);
    } catch (cause: unknown) {
      setError(describeArtistSettlementsError(cause));
    } finally {
      setWorking(null);
    }
  }, []);

  return {
    view: toArtistSettlementsView({ settlements, error, images, workBase }),
    working,
    disputing,
    openDispute: setDisputing,
    closeDispute: () => setDisputing(null),
    confirm: (settlementId) => {
      void run(settlementId, () => answerSettlement(settlementId, { confirm: true }));
    },
    dispute: (settlementId, note) => {
      void run(settlementId, () =>
        // Trimmed to null so whitespace is not a reason. Disputing needs no
        // reason at all — requiring one would make disagreeing the harder of
        // the two acts, and this is the artist's only control.
        answerSettlement(settlementId, {
          confirm: false,
          note: note.trim() === "" ? null : note.trim(),
        }),
      );
    },
  };
}
